import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Progress } from "./ui/progress";
import { Alert, AlertDescription } from "./ui/alert";
import { HardDrive, Loader2, AlertTriangle, AlertCircle } from 'lucide-react';
import systemService from '../services/systemService';
import { cn } from '../lib/utils';

const ServerStorageCard = () => {
    const [storageData, setStorageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadStorageData();

        // Auto-refresh every 30 seconds
        const interval = setInterval(() => {
            loadStorageData();
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const loadStorageData = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await systemService.getServerStorage();
            setStorageData(response.storage);
        } catch (err) {
            console.error('Error loading server storage:', err);
            setError('Gagal memuat informasi storage server');
        } finally {
            setLoading(false);
        }
    };

    if (loading && !storageData) {
        return (
            <Card className="h-full flex items-center justify-center min-h-[200px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="h-full border-destructive/50 bg-destructive/5">
                <CardContent className="pt-6">
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    if (!storageData) {
        return null;
    }

    // Determine status color/state
    const isCritical = storageData.usedPercentage > 90;
    const isWarning = storageData.usedPercentage > 75;

    let statusColor = "bg-primary";
    if (isCritical) statusColor = "bg-destructive";
    else if (isWarning) statusColor = "bg-yellow-500";

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <div className={cn("p-2 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-200")}>
                            <HardDrive className="h-5 w-5" />
                        </div>
                        <div>
                            <CardTitle className="text-base font-semibold">Server Storage</CardTitle>
                            <p className="text-xs text-muted-foreground font-mono">{storageData.mountedOn}</p>
                        </div>
                    </div>
                    {isWarning && (
                        <div className={cn(
                            "px-2 py-1 rounded text-xs font-bold flex items-center gap-1",
                            isCritical ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200" : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200"
                        )}>
                            <AlertTriangle className="h-3 w-3" />
                            {isCritical ? 'CRITICAL' : 'WARNING'}
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-end pt-0 pb-6">
                <div className="mb-4 mt-2">
                    <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-4xl font-bold tracking-tight">{Math.round(storageData.usedPercentage)}%</span>
                        <span className="text-sm text-muted-foreground font-medium">Terpakai</span>
                    </div>
                </div>

                <div className="space-y-3">
                    <Progress value={storageData.usedPercentage} className="h-3" indicatorClassName={statusColor} />

                    <div className="flex justify-between items-center bg-secondary/50 p-2 rounded-md text-sm">
                        <div>
                            <span className="text-xs text-muted-foreground block">Terpakai</span>
                            <span className="font-semibold">{storageData.used}</span>
                        </div>
                        <div className="text-right">
                            <span className="text-xs text-muted-foreground block">Total</span>
                            <span className="font-semibold">{storageData.total}</span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default ServerStorageCard;
